import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { completeGithubInstallation } from '@kirin/api-client/api/github';
import { SettingType } from '@kirin/api-client/contracts/settings';
import { apiClient } from '@/lib/api-client';
import { debugLog } from '@/lib/debug';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useRoleCheck, useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useGithubStatus } from '@/hooks/api/useGithub';
import { useOrgSetting, useUpsertOrgSetting } from '@/hooks/api/useOrgSettings';
import { useToast } from '@/hooks/use-toast';
import { useScimProvisioningIntegrationAccess } from '@/hooks/useIntegrationAccess';
import { SettingsPageLayout } from '../SettingsPageLayout';
import { SettingsTabContent } from '../SettingsTabContent';
import type { OrgTabKey } from '../settingsTabs';
import { OrganizationSubTab } from './OrganizationSubTab';
import { OrganizationIntegrationsPanel } from '../OrganizationIntegrationsPanel';
import { NotificationsSubTab } from './NotificationsSubTab';
import { PreferencesSubTab } from '../PreferencesSubTab';

export function OrganizationSettingsContent(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const githubCallbackProcessed = useRef(false);
  useEffect(() => {
    const handleGithubCallback = async (): Promise<void> => {
      const installationId = searchParams.get('installation_id');
      const setupAction = searchParams.get('setup_action');
      const state = searchParams.get('state');

      if (
        installationId &&
        state &&
        organizationId &&
        !githubCallbackProcessed.current
      ) {
        githubCallbackProcessed.current = true;
        debugLog('[GitHub] Installation callback received', {
          installationId,
          setupAction,
          hasState: !!state,
          organizationId,
        });

        try {
          const result = await completeGithubInstallation(
            apiClient,
            organizationId,
            {
              installation_id: installationId,
              state: state,
              setup_action: setupAction,
            }
          );

          if ('enabled' in result && result.enabled === false) {
            debugLog('[GitHub] Feature flag is disabled, skipping completion');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('installation_id');
            newSearchParams.delete('setup_action');
            newSearchParams.delete('state');
            setSearchParams(newSearchParams, { replace: true });
            return;
          }

          debugLog('[GitHub] Installation completed successfully');

          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('installation_id');
          newSearchParams.delete('setup_action');
          newSearchParams.delete('state');
          newSearchParams.set('github', 'connected');
          setSearchParams(newSearchParams, { replace: true });
        } catch (error) {
          console.error('[GitHub] Failed to complete installation:', error);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('installation_id');
          newSearchParams.delete('setup_action');
          newSearchParams.delete('state');
          newSearchParams.set('github', 'error');
          setSearchParams(newSearchParams, { replace: true });
          githubCallbackProcessed.current = false;
        }
      }
    };

    void handleGithubCallback();
  }, [searchParams, setSearchParams, organizationId]);

  // Feature flags
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SLACK_SCAN_LOGS_EVENTS,
    flagsmithFeatureFlags.GITHUB_INTEGRATION,
    flagsmithFeatureFlags.BITBUCKET_INTEGRATION,
    flagsmithFeatureFlags.SSO_LOGIN,
    flagsmithFeatureFlags.SIEM_INTEGRATION,
    flagsmithFeatureFlags.SHOW_DATA_COLLECTION_SETTINGS,
  ]);
  const isSlackEnabled =
    flags[flagsmithFeatureFlags.SLACK_SCAN_LOGS_EVENTS] ?? true;
  const isGithubIntegrationEnabled =
    import.meta.env.VITE_FF_GITHUB_INTEGRATION === 'true' ||
    flags[flagsmithFeatureFlags.GITHUB_INTEGRATION] ||
    false;
  const isBitbucketIntegrationEnabled =
    import.meta.env.VITE_FF_BITBUCKET_INTEGRATION === 'true' ||
    flags[flagsmithFeatureFlags.BITBUCKET_INTEGRATION] ||
    false;
  const isSSOEnabled =
    import.meta.env.VITE_FF_OKTA_SSO_LOGIN === 'true' ||
    flags[flagsmithFeatureFlags.SSO_LOGIN] ||
    false;
  const isSiemIntegrationEnabled =
    flags[flagsmithFeatureFlags.SIEM_INTEGRATION] || false;
  // SCIM is role-gated (no flag), so it's absent from the flag list above. Fold
  // it into hasIntegrations so a SCIM-only org (every other integration flag off)
  // can still reach the Integrations tab. Gate on canRender (not isLoading) so the
  // tab doesn't flash for orgs that ultimately have no integrations (KIR-4441).
  const scimAccess = useScimProvisioningIntegrationAccess();
  const isScimIntegrationEnabled = scimAccess.canRender;
  const isDataCollectionSettingsEnabled =
    flags[flagsmithFeatureFlags.SHOW_DATA_COLLECTION_SETTINGS] ?? false;

  // RBAC
  const { hasAccess: canModifySettings } = useRoleCheck(
    { param: 'organizationId', anyOf: ['owner', 'manager'] },
    organizationId
  );
  const { hasAccess: isOwner } = useRoleCheck(
    { param: 'organizationId', anyOf: ['owner'] },
    organizationId
  );
  const { canViewAdminContent } = useCanViewAdminContent(organizationId);

  // Org-level setting: data collection
  const orgDataCollectionQuery = useOrgSetting(
    organizationId || '',
    SettingType.DataCollection,
    canModifySettings && isDataCollectionSettingsEnabled
  );
  const upsertOrgDataCollectionMutation = useUpsertOrgSetting(
    organizationId || '',
    SettingType.DataCollection
  );
  const orgDataCollectionEnabled =
    (orgDataCollectionQuery.data?.config as { storeInDb?: boolean } | undefined)
      ?.storeInDb ?? true;
  const handleOrgDataCollectionChange = useCallback(
    async (storeInDb: boolean): Promise<void> => {
      try {
        await upsertOrgDataCollectionMutation.mutateAsync({
          body: { config: { storeInDb } },
        });
        toast({
          title: 'Settings saved',
          description: storeInDb
            ? "Your organization's data sharing is now enabled"
            : "Your organization's data sharing is now disabled",
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save data collection preference',
        });
      }
    },
    [upsertOrgDataCollectionMutation, toast]
  );

  // Org-level setting: inventory visibility (hide stale entries after N days)
  const orgInventoryVisibilityQuery = useOrgSetting(
    organizationId || '',
    SettingType.InventoryVisibility,
    canModifySettings
  );
  const upsertOrgInventoryVisibilityMutation = useUpsertOrgSetting(
    organizationId || '',
    SettingType.InventoryVisibility
  );
  const orgInventoryVisibilityConfig = orgInventoryVisibilityQuery.data
    ?.config as { hideAfterDays?: number | null } | undefined;
  // Distinguish "not loaded yet" (→ product default 60) from an explicit
  // `null` ("Never") — `?? 60` would wrongly coerce Never into 60.
  const orgInventoryHideAfterDays: number | null = orgInventoryVisibilityConfig
    ? (orgInventoryVisibilityConfig.hideAfterDays ?? null)
    : 60;
  const handleOrgInventoryVisibilityChange = useCallback(
    async (hideAfterDays: number | null): Promise<void> => {
      try {
        await upsertOrgInventoryVisibilityMutation.mutateAsync({
          body: { config: { hideAfterDays } },
        });
        toast({
          title: 'Settings saved',
          description: 'Inventory visibility updated successfully',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save inventory visibility setting',
        });
      }
    },
    [upsertOrgInventoryVisibilityMutation, toast]
  );

  // GitHub status
  const { data: githubStatus, refetch: refetchGithubStatus } = useGithubStatus(
    organizationId || ''
  );
  const isGithubConnected = githubStatus?.connected === true;

  // Lives at the container so toast+refetch fire regardless of active sub-tab; trusts
  // ?github=connected|error from the install effect above and forces ?tab=integrations.
  // Any other ?github value is scrubbed silently so the URL self-heals.
  useEffect(() => {
    const githubState = searchParams.get('github');
    if (githubState === null) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('github');

    if (githubState === 'connected') {
      void refetchGithubStatus();
      toast({
        title: 'GitHub connected',
        description: 'Successfully connected! Syncing organization members…',
      });
      newParams.set('tab', 'integrations');
    } else if (githubState === 'error') {
      toast({
        title: 'GitHub connection failed',
        description:
          'Failed to complete GitHub setup. The link may have expired or already been used. Please try again.',
        variant: 'destructive',
      });
      newParams.set('tab', 'integrations');
    }

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, refetchGithubStatus, toast]);

  const hasIntegrations =
    isGithubIntegrationEnabled ||
    isSlackEnabled ||
    isSSOEnabled ||
    isSiemIntegrationEnabled ||
    isBitbucketIntegrationEnabled ||
    isScimIntegrationEnabled;

  const hiddenTabs = useMemo<OrgTabKey[]>(() => {
    const hidden: OrgTabKey[] = [];
    if (!canViewAdminContent || !hasIntegrations) {
      hidden.push('integrations');
    }
    return hidden;
  }, [canViewAdminContent, hasIntegrations]);

  if (!organizationId) {
    // console.error so Sentry's captureConsoleIntegration picks up the misuse.
    console.error(
      'OrganizationSettingsContent rendered outside :organizationId route'
    );
    return null;
  }

  return (
    <SettingsPageLayout context="organization" hiddenTabs={hiddenTabs}>
      <SettingsTabContent
        value="organization"
        context="organization"
        hiddenTabs={hiddenTabs}
      >
        <OrganizationSubTab
          organizationId={organizationId}
          canModifySettings={canModifySettings}
          isOwner={isOwner}
          isDataCollectionSettingsEnabled={isDataCollectionSettingsEnabled}
          isGithubIntegrationEnabled={isGithubIntegrationEnabled}
          isGithubConnected={isGithubConnected}
          orgDataCollectionEnabled={orgDataCollectionEnabled}
          handleOrgDataCollectionChange={handleOrgDataCollectionChange}
          upsertOrgDataCollectionMutationPending={
            upsertOrgDataCollectionMutation.isPending
          }
          orgInventoryHideAfterDays={orgInventoryHideAfterDays}
          handleOrgInventoryVisibilityChange={
            handleOrgInventoryVisibilityChange
          }
          upsertOrgInventoryVisibilityMutationPending={
            upsertOrgInventoryVisibilityMutation.isPending
          }
        />
      </SettingsTabContent>
      <SettingsTabContent
        value="integrations"
        context="organization"
        hiddenTabs={hiddenTabs}
      >
        <OrganizationIntegrationsPanel />
      </SettingsTabContent>
      <SettingsTabContent
        value="notifications"
        context="organization"
        hiddenTabs={hiddenTabs}
      >
        <NotificationsSubTab organizationId={organizationId} />
      </SettingsTabContent>
      <SettingsTabContent
        value="preferences"
        context="organization"
        hiddenTabs={hiddenTabs}
      >
        <PreferencesSubTab />
      </SettingsTabContent>
    </SettingsPageLayout>
  );
}
